import { CardMedia, Typography, Box, Stack, Grid } from '@mui/material';
import { Product } from '../../data/products';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface CatalogCardProps {
  product: Product;
  index: number;
}

export const CatalogCard = ({ product, index }: CatalogCardProps) => {
  const isEven = index % 2 === 0;

  return (
    <Box 
      className="product-card-container"
      sx={{ 
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', md: isEven ? 'row' : 'row-reverse' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1, md: 2 }, 
        padding: 0,
        breakInside: 'avoid',
        // Fondo decorativo de Scrapbook para llenar el vacío
        backgroundImage: isEven 
          ? 'radial-gradient(circle at 80% 50%, rgba(255, 183, 197, 0.2) 0%, transparent 60%)'
          : 'radial-gradient(circle at 20% 50%, rgba(255, 183, 197, 0.2) 0%, transparent 60%)',
        '@media print': {
          flexDirection: isEven ? 'row !important' : 'row-reverse !important',
          padding: '0 !important',
          gap: '8mm !important',
          backgroundImage: 'none' // Limpio para impresión si se desea ahorrar tinta
        },
        '&:hover .sticker': { transform: 'scale(1.1) rotate(5deg)' }
      }}
    >
      {/* Puntos decorativos (Dot Grid) para llenar el espacio horizontal */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: isEven ? 'auto' : '10%',
        right: isEven ? '10%' : 'auto',
        transform: 'translateY(-50%)',
        width: '300px',
        height: '150px',
        backgroundImage: 'radial-gradient(#E08EA0 1px, transparent 1px)',
        backgroundSize: '15px 15px',
        opacity: 0.15,
        zIndex: 0,
        display: { xs: 'none', md: 'block' }
      }} />

      {/* Elementos Flotantes Scrapbook */}
      <Box 
        className="floating-element sticker"
        sx={{
          position: 'absolute',
          top: -20,
          left: isEven ? '15%' : 'auto',
          right: isEven ? 'auto' : '15%',
          color: 'secondary.main',
          opacity: 0.4,
          transition: 'all 0.3s ease',
          zIndex: 1,
          transform: isEven ? 'rotate(-15deg)' : 'rotate(15deg)'
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 40 }} />
      </Box>

      <Box 
        className="floating-element sticker"
        sx={{
          position: 'absolute',
          bottom: -10,
          left: isEven ? 'auto' : '5%',
          right: isEven ? '5%' : 'auto',
          color: 'primary.main',
          opacity: 0.3,
          transition: 'all 0.3s ease',
          zIndex: 1,
          transform: isEven ? 'rotate(20deg)' : 'rotate(-20deg)',
          display: { xs: 'none', md: 'block' }
        }}
      >
        <FavoriteIcon sx={{ fontSize: 30 }} />
      </Box>

      {/* Imagen con máscara circular compacta */}
      <Box sx={{ 
        position: 'relative',
        width: { xs: 150, sm: 200, md: 280 },
        height: { xs: 150, sm: 200, md: 280 },
        flexShrink: 0,
        zIndex: 2,
        '@media print': {
          width: '65mm !important',
          height: '65mm !important'
        }
      }}>
        <Box 
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '6px solid white',
            boxShadow: '0px 10px 30px rgba(224, 142, 160, 0.3)',
            userSelect: 'none', 
            pointerEvents: 'auto'
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <CardMedia
            component="img"
            image={product.imageUrl}
            alt={product.name}
            draggable={false}
            sx={{ 
              height: '100%', 
              width: '100%', 
              objectFit: 'cover',
              WebkitUserDrag: 'none'
            }}
          />
        </Box>
        
        {/* Logo 3 Corazones reemplazando el círculo 3C */}
        <Box 
          className="sticker"
          sx={{
            position: 'absolute',
            bottom: 15,
            right: 5,
            width: { xs: 40, sm: 50, md: 70 },
            height: { xs: 40, sm: 50, md: 70 },
            bgcolor: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid white',
            boxShadow: '0px 4px 15px rgba(224, 142, 160, 0.4)',
            zIndex: 3,
          }}
        >
          <Box 
            component="img"
            src="/logo.png"
            sx={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              mixBlendMode: 'multiply'
            }}
          />
        </Box>
      </Box>

      {/* Contenido Tipográfico Compacto */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'center', md: isEven ? 'flex-start' : 'flex-end' },
          textAlign: { xs: 'center', md: isEven ? 'start' : 'end' }, 
          '@media print': {
            alignItems: isEven ? 'flex-start !important' : 'flex-end !important',
            textAlign: isEven ? 'start !important' : 'end !important'
          },
          zIndex: 3 
        }}
      >
        <Typography 
          variant="h2" 
          sx={{ 
            fontSize: { xs: '1.5rem', md: '2.2rem' },
            lineHeight: 1.1,
            mb: 0.5, 
            color: 'secondary.dark',
            textTransform: 'uppercase',
            fontFamily: 'Playfair Display',
            textAlign: 'inherit',
            '@media print': {
              fontSize: '22pt !important',
              textAlign: 'inherit'
            }
          }}
        >
          {product.name}
        </Typography>
        <Typography 
          variant="h4" 
          color="primary" 
          sx={{ 
            fontWeight: 900, 
            mb: 1, 
            fontSize: { xs: '1.2rem', md: '1.8rem' },
            textAlign: 'inherit'
          }}
        >
          S/ {product.price}.00
        </Typography>

        <Grid 
          container 
          spacing={5} 
          sx={{ 
            justifyContent: { xs: 'center', md: isEven ? 'start' : 'end' },
            '@media print': {
              justifyContent: isEven ? 'start !important' : 'end !important'
            }
          }} 
        >
          {product.available?.length && (
            <Grid size='auto'>
                <Typography variant="overline" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', display: 'block', textAlign: 'inherit' }}>
                  Disponible en:
                </Typography>
                
                <Box sx={{ mb: 1.5 }}>
                  {product.available.map((feature) => (
                    <Typography 
                      key={feature} 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 0.2,
                        display: 'block',
                        textAlign: 'inherit'
                      }}
                    >
                      • {feature}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )
          }
          {
            product.features.length > 0 && (
              <Grid>
                <Typography variant="overline" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', display: 'block', textAlign: 'inherit' }}>
                  INCLUYE:
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  {product.features.map((feature) => (
                    <Typography 
                      key={feature} 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 0.2,
                        display: 'block',
                        textAlign: 'inherit'
                      }}
                    >
                      • {feature}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )
            }
        </Grid>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            maxWidth: 400, 
            textAlign: 'inherit',
            '@media print': {
              textAlign: 'inherit',
              fontSize: '11pt !important'
            },
            lineHeight: 1.4,
            fontStyle: 'italic'
          }}
        >
          {product.description}
        </Typography>
      </Box>
    </Box>
  );
};
